import Vue from 'vue'
import include from '@/views/boards/single-page/include.js'
import store from '@/store/store'

const titleColumnNames = {
  813: '소분류',
  814: '소분류',
  845: '공사/작업명',
  899: '활동명',
}

// 813 -> 736(공정분류표(직영)), 814 -> 895(공정분류표(수급업체))
// raw.소분류(문서 저장 시점 스냅샷)는 736/895가 리네임되면 옛 이름으로 남으므로,
// 최신 이름을 비동기로 가져와 아래 reactive 캐시에 채워 넣는다.
const classificationBoardByBoardId = {
  813: 736,
  814: 895,
}

// "classificationBoardId:rawId" -> 최신 소분류 (string) | null(조회 실패/없음)
// Vue.observable 이라 이 객체의 프로퍼티를 렌더링 중에 읽으면, 값이 나중에 채워질 때 자동으로 다시 렌더링된다.
const latestCache = Vue.observable({})
const queued = {} // classificationBoardId -> Set(rawId)
const flushScheduled = {}

function scheduleFlush(classificationBoardId) {
  if (flushScheduled[classificationBoardId]) return
  flushScheduled[classificationBoardId] = true

  Promise.resolve().then(async () => {
    const ids = Array.from(queued[classificationBoardId] || [])
    queued[classificationBoardId] = new Set()
    flushScheduled[classificationBoardId] = false
    if (ids.length === 0) return

    try {
      const response = await store.dispatch('board/LOAD_POST_LIST', {
        boardId: classificationBoardId,
        page: 1,
        perPage: ids.length,
        postIds: ids,
      })

      const found = new Set()
      for (const post of (response && response.data) || []) {
        try {
          const body = JSON.parse(post.post_content).body
          Vue.set(latestCache, `${classificationBoardId}:${post.id}`, (body && body.소분류) || null)
          found.add(Number(post.id))
        } catch (e) {
          // 개별 항목 파싱 실패는 무시하고 계속 진행
        }
      }
      // 응답에 없는(삭제된 등) id는 null로 채워서 재요청을 막는다
      for (const id of ids) {
        if (!found.has(id)) Vue.set(latestCache, `${classificationBoardId}:${id}`, null)
      }
    } catch (e) {
      for (const id of ids) {
        Vue.set(latestCache, `${classificationBoardId}:${id}`, null)
      }
    }
  })
}

function requestLatest(classificationBoardId, rawId) {
  if (!queued[classificationBoardId]) queued[classificationBoardId] = new Set()
  if (queued[classificationBoardId].has(rawId)) return
  queued[classificationBoardId].add(rawId)
  scheduleFlush(classificationBoardId)
}

export default function getPostTitle(data) {
  const boardId = data.board_id || (data.post && data.post.board_id)
  if (!boardId) return ''

  const columnName = titleColumnNames[boardId]
  if (!columnName) return ''

  const listConfig = include.list[`${boardId}_list`]
  if (!listConfig) return ''

  const heads = listConfig.getHead()
  const titleHead = heads.find(h => h.name === columnName)
  if (!titleHead || !titleHead.key) return ''

  let rawTitle = ''
  let content
  try {
    content = JSON.parse(data.post_content)
    let cursor = content
    const keys = titleHead.key.split('.')
    for (const key of keys) {
      cursor = cursor[key]
      if (cursor === undefined || cursor === null) {
        cursor = ''
        break
      }
    }
    rawTitle = typeof cursor === 'string' ? cursor : ''
  } catch (e) {
    return ''
  }

  // 813/814는 raw.소분류가 736/895 개정 이후 옛 이름일 수 있으므로, 최신 이름으로 보정을 시도한다.
  const classificationBoardId = classificationBoardByBoardId[boardId]
  if (classificationBoardId) {
    const rawId = content && content.body && content.body.공정분류 && content.body.공정분류.raw && content.body.공정분류.raw.id
    if (rawId) {
      const cacheKey = `${classificationBoardId}:${rawId}`
      if (!(cacheKey in latestCache)) {
        // Vue 2 반응성 한계: 존재하지 않는 프로퍼티를 읽는 것만으로는 의존성이 등록되지 않으므로,
        // 먼저 Vue.set으로 자리를 만들어둔 뒤에 읽어야 나중에 값이 채워질 때 재렌더링된다.
        Vue.set(latestCache, cacheKey, undefined)
        requestLatest(classificationBoardId, rawId)
      }
      const cached = latestCache[cacheKey] // 항상 읽어서 reactive dependency를 등록시킨다
      if (cached) return cached
    }
  }

  return rawTitle
}
